import { ContentService } from '@blk/cwp-ng-framework';
import { CellData, CellUtil, ColumnConfig, ColumnText, ComponentData, TabData } from '@ddt-web/data-service';
import { WebUserDataConstants } from '@ddt-web/domain';
import { DcrUpdateUtil } from '@ddt-web/generic-utils';
import * as get from 'lodash/get';
import * as isNil from 'lodash/isNil';

import { PerformanceSection } from '../models/performance-section.interface';
import { HypotheticalGrowthValueService } from '../services/hypothetical-growth-value.service';

export class PerformanceUtil {
	private static grossPerformanceIds = ['performanceReturn', 'performanceDownsideUpside', 'performanceDownsideUpsideBenchmark'];
	private static netCustomPerformanceIds = ['netCustomPerformanceReturn', 'netCustomPerformanceDownsideUpside', 'netCustomPerformanceDownsideUpsideBenchmark'];

	public static updateNetCustomPerformanceCells(tab: TabData, advisoryFee: number): TabData {
		(tab?.tableData?.[0]?.cells ?? []).forEach(cellData => {
			const netPerformanceCell = cellData.find(cell => cell.config.id === 'netCustomPerformanceType');

			if (netPerformanceCell) {
				netPerformanceCell.attribute['advisoryFee'] = advisoryFee;
			}
		});

		return tab;
	}

	public static updateSnapshotPerformanceCells(tab: TabData, contentService: ContentService, customAdvisoryFee?: number): TabData {
		get(tab, 'tableData[0].cells', []).forEach(cellData => {
			cellData.filter(cell => PerformanceUtil.grossPerformanceIds.includes(cell.config.id)).forEach(cell => {
				cell.config.infoBubble = null;
			});

			if (customAdvisoryFee) {
				cellData.filter(cell => PerformanceUtil.netCustomPerformanceIds.includes(cell.config.id)).forEach(cell => {
					cell.attribute['optionalLabel'] = DcrUpdateUtil.getAdvisoryFeeText(contentService, customAdvisoryFee);
				});
			}
		});

		return tab;
	}

	public static getSectionTitleCells(performanceColumns: Array<string | PerformanceSection>, contentService: ContentService): { [key: string]: CellData } {
		const titleCells: { [key: string]: CellData } = {};

		performanceColumns.filter(column => get(column, 'sectionKey')).forEach(column => {

			const labelKey = get(column, 'sectionKey');
			const infoBubbleKey = get(column, 'sectionKey') + '_INFO_BUBBLE';

			const labelCell = new CellData(
				new ColumnConfig(
					new ColumnText(labelKey, labelKey, contentService.get(labelKey, labelKey)),
					new ColumnText(infoBubbleKey, infoBubbleKey, contentService.get(infoBubbleKey, infoBubbleKey)),
					null,
					labelKey
				),
				labelKey
			);

			titleCells[labelKey] = labelCell;
		});

		return titleCells;
	}

	public static getPerformanceAsOfDate(grossTabData: TabData, asOfDate: string): string {
		if (!asOfDate) {
			return '';
		}

		for (const tableData of (grossTabData?.tableData ?? [])) {
			for (const row of tableData.cells) {
				const performanceAsOfDate = CellUtil.getById(row, 'performanceAsOfDate');
				if (performanceAsOfDate && performanceAsOfDate.formattedValue !== asOfDate) {
					return performanceAsOfDate.formattedValue;
				}
			}
		}

		return '';
	}

	public static getFilteredPortfolioData(portfolioData: CellData[][], filterAssetClasses: boolean, filterInvalidPortfolioData: boolean): CellData[][] {
		let filteredPortfolioData = portfolioData;
		if (filterAssetClasses) {
			const portfolioTypeColIndex = portfolioData[0].findIndex(column => column.config.id === 'portfolioType');
			filteredPortfolioData = filteredPortfolioData.filter(portfolio => portfolio[portfolioTypeColIndex]?.rawValue !== WebUserDataConstants.SUBTYPE.ASSET_CLASS);
		}
		if (filterInvalidPortfolioData) {
			const idColIndex = portfolioData[0].findIndex(column => column.config.id === 'id');
			filteredPortfolioData = filteredPortfolioData.filter(portfolio => !isNil(portfolio[idColIndex].rawValue));
		}

		return filteredPortfolioData;
	}


	public static getHypoChart(tabData: TabData, initialStartDate: any): TabData {
		return tabData && initialStartDate?.isValid() ? HypotheticalGrowthValueService.prepareAccumulativeReturns(tabData, initialStartDate) : tabData;
	}

	public static getPortfolioPerfTableData(validTableComponentDataIds: string[], data: ComponentData, customAdvisoryFee: number): [tabs: TabData[], columnWidths: number[]] {
		// get tabs to populate table
		const tabs = data.tabs.filter(tab => validTableComponentDataIds.includes(tab.config.id)).map(tab => {
			if (tab.config.id === 'hypo-net-custom-performance') {
				// add advisory fee to net performance type cell
				return PerformanceUtil.updateNetCustomPerformanceCells(tab, customAdvisoryFee);
			}
			return tab;
		});

		// set column width for columns
		const visibleHeadersX = get(tabs, '[0].tableData[0].headersX', []).filter(header => get(header, 'config.visible', true));
		const totalColumns = get(tabs, '[0].tableData[0].firstColumnTitle') ? visibleHeadersX.length + 1 : visibleHeadersX.length;

		// set columnWidths based on total number of columns; this needs to sum up to 100
		let columnWidths: number[];
		switch (totalColumns) {
			case 7:
				columnWidths = [25, 15, 15, 15, 10, 10, 10];
				break;
			case 8:
				columnWidths = [21, 22, 11, 11, 11, 8, 8, 8];
				break;
			case 9:
				columnWidths = [18, 19, 9, 9, 9, 9, 9, 9, 9];
				break;
			case 11:
				columnWidths = [14, 10, 8, 9, 9, 8, 8, 8, 8, 9, 9];
				break;
		}
		return [tabs, columnWidths];
	}
}
